import { useCallback, useRef } from "react";
import type { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import {
  createRangeUnion,
  useContentProgress,
} from "../../tools/analytics/useContentProgress";
import type { ContentExitAction } from "../../tools/analytics/base.analytics";
import { infographicSource } from "../../domains/infographic/infographicSource";

/**
 * How a surface shows the infographic, which decides what 100% means:
 * - `image-pan`: the phone's pinch viewer; the share of the image's height
 *   that passed through the frame, zoom included.
 * - `image-fitted`: the desktop viewer, which opens on the whole image.
 * - `pdf-scroll`: the phone's continuous PDF; the scroll depth.
 * - `pdf-paged`: the desktop PDF, one page at a time; pages seen over pages.
 * - `embed`: a page in a frame, out of reach; whole once looked at.
 * Whatever is on screen counts once the reader stayed `SETTLE_MS` on it.
 */
type InfographicView =
  "image-pan" | "image-fitted" | "pdf-scroll" | "pdf-paged" | "embed" | "none";

function infographicView(
  infographic: Parameters<typeof infographicSource>[0],
  surface: "mobile" | "desktop"
): InfographicView {
  const source = infographicSource(infographic);
  switch (source.kind) {
    case "image":
      return surface === "mobile" ? "image-pan" : "image-fitted";
    case "pdf":
      return surface === "mobile" ? "pdf-scroll" : "pdf-paged";
    case "embed":
      return "embed";
    case "none":
      return "none";
    default:
      throw new Error(source satisfies never);
  }
}

function clampFraction(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/** The band of the image inside the pinch viewer's frame, as fractions. */
function visibleBand(ref: ReactZoomPanPinchRef): [number, number] | null {
  const { wrapperComponent, contentComponent } = ref.instance;
  if (wrapperComponent === null || contentComponent === null) return null;
  const frame = wrapperComponent.getBoundingClientRect();
  const image = contentComponent.getBoundingClientRect();
  // An image still loading has no height to divide by.
  if (image.height === 0) return null;
  return [
    clampFraction((frame.top - image.top) / image.height),
    clampFraction((frame.bottom - image.top) / image.height),
  ];
}

export function useInfographicProgress(params: {
  infographic:
    (Parameters<typeof infographicSource>[0] & { id: string }) | null;
  surface: "mobile" | "desktop";
}): {
  onImageView: (ref: ReactZoomPanPinchRef) => void;
  onContentLoad: () => void;
  onPdfScroll: (element: HTMLElement) => void;
  onPdfPage: (page: number, count: number) => void;
  noteExit: (action: ContentExitAction) => void;
} {
  const { infographic, surface } = params;
  const view: InfographicView =
    infographic === null ? "none" : infographicView(infographic, surface);
  const seenRef = useRef(createRangeUnion());
  const settledRef = useRef(false);
  // Nothing counts before the infographic shows: an empty PDF box or a frame
  // still loading would read as consumed whole.
  const loadedRef = useRef(false);
  // Waits for the first drawn page of a mobile PDF; one per open at most.
  const pdfDrawnRef = useRef<MutationObserver | null>(null);

  function stopWaitingForPdf() {
    if (pdfDrawnRef.current === null) return;
    pdfDrawnRef.current.disconnect();
    pdfDrawnRef.current = null;
  }
  const imageViewRef = useRef<ReactZoomPanPinchRef | null>(null);
  const pdfScrollRef = useRef<HTMLElement | null>(null);
  const pdfPagesRef = useRef<{ seen: Set<number>; count: number } | null>(null);

  function measure() {
    if (!loadedRef.current) return;
    switch (view) {
      case "image-pan": {
        const ref = imageViewRef.current;
        if (ref === null) return;
        const band = visibleBand(ref);
        if (band === null) return;
        seenRef.current.add(band[0], band[1]);
        progress.reportPercent(seenRef.current.length() * 100);
        return;
      }
      case "image-fitted":
      case "embed":
        progress.reportPercent(100);
        return;
      case "pdf-scroll": {
        const element = pdfScrollRef.current;
        if (element === null) return;
        // PdfViewer reports its load before it draws a page, and on a failure
        // too: until a page is drawn, the empty box reads as fully scrolled.
        // The first drawn page measures again.
        if (element.querySelector("canvas") === null) {
          if (pdfDrawnRef.current !== null) return;
          const drawn = new MutationObserver(() => {
            if (element.querySelector("canvas") === null) return;
            stopWaitingForPdf();
            // Before the settle, the settle measures it.
            if (settledRef.current) measureRef.current();
          });
          drawn.observe(element, { childList: true, subtree: true });
          pdfDrawnRef.current = drawn;
          return;
        }
        progress.reportPercent(
          ((element.scrollTop + element.clientHeight) / element.scrollHeight) *
            100
        );
        return;
      }
      case "pdf-paged": {
        const pages = pdfPagesRef.current;
        if (pages === null) return;
        progress.reportPercent((pages.seen.size / pages.count) * 100);
        return;
      }
      case "none":
        return;
      default:
        throw new Error(view satisfies never);
    }
  }

  const progress = useContentProgress({
    subject:
      infographic === null
        ? null
        : { contentType: "infographic", contentId: infographic.id },
    endOnHidden: true,
    onStart: (start) => {
      settledRef.current = false;
      stopWaitingForPdf();
      if (start === "resumed") return;
      loadedRef.current = false;
      seenRef.current = createRangeUnion();
      pdfPagesRef.current = null;
    },
    onSettled: () => {
      settledRef.current = true;
      measure();
    },
    onFinish: () => {
      stopWaitingForPdf();
      return {
        mediaDurationSeconds: null,
        playlistItems: null,
        article: null,
      };
    },
  });

  const measureRef = useRef(measure);
  measureRef.current = measure;
  const measureOnceSettled = useCallback(() => {
    if (settledRef.current) measureRef.current();
  }, []);

  const onImageView = useCallback(
    (ref: ReactZoomPanPinchRef) => {
      imageViewRef.current = ref;
      measureOnceSettled();
    },
    [measureOnceSettled]
  );

  const onPdfScroll = useCallback(
    (element: HTMLElement) => {
      pdfScrollRef.current = element;
      measureOnceSettled();
    },
    [measureOnceSettled]
  );

  const onPdfPage = useCallback(
    (page: number, count: number) => {
      const pages =
        pdfPagesRef.current === null
          ? { seen: new Set<number>(), count }
          : pdfPagesRef.current;
      pages.seen.add(page);
      pages.count = count;
      pdfPagesRef.current = pages;
      // The document reported its page count: it is shown.
      loadedRef.current = true;
      measureOnceSettled();
    },
    [measureOnceSettled]
  );

  const onContentLoad = useCallback(() => {
    loadedRef.current = true;
    measureOnceSettled();
  }, [measureOnceSettled]);

  return {
    onImageView,
    onContentLoad,
    onPdfScroll,
    onPdfPage,
    noteExit: progress.noteExit,
  };
}
